import { Button, Card, Input, Switch, Typography } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useState, useRef, useEffect } from "react";

const { Title, Text } = Typography;

export default function QueueManagementPanel({
  businessName,
  currentNumber,
  queueStatus,
  handleIncrement,
  handleDecrement,
  handleReset,
  setNumber: handleNumberChange, // Add this new prop to handle number updates
  toggleActiveStatus,
  setBusinessMessageForUsers,
  businessMessage,
}) {
  const [textValue, setText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(currentNumber);
  const [loadingStates, setLoadingStates] = useState({
    increment: false,
    decrement: false,
    reset: false,
    numberChange: false,
  });
  const inputRef = useRef(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleNumberClick = () => {
    setEditValue(currentNumber);
    setIsEditing(true);
  };

  const handleNumberChangeConfirm = async () => {
    const num = parseInt(editValue);
    if (!isNaN(num) && num >= 0) {
      setLoadingStates(prev => ({ ...prev, numberChange: true }));
      try {
        await handleNumberChange(num);
      } finally {
        setLoadingStates(prev => ({ ...prev, numberChange: false }));
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNumberChangeConfirm();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const handleIncrementWithLoading = async () => {
    setLoadingStates(prev => ({ ...prev, increment: true }));
    try {
      await handleIncrement();
    } finally {
      setLoadingStates(prev => ({ ...prev, increment: false }));
    }
  };

  const handleDecrementWithLoading = async () => {
    setLoadingStates(prev => ({ ...prev, decrement: true }));
    try {
      await handleDecrement();
    } finally {
      setLoadingStates(prev => ({ ...prev, decrement: false }));
    }
  };

  const handleResetWithLoading = async () => {
    setLoadingStates(prev => ({ ...prev, reset: true }));
    try {
      await handleReset();
    } finally {
      setLoadingStates(prev => ({ ...prev, reset: false }));
    }
  };

  return (
    <Card className="queue-management-panel">
      <Title
        level={3}
        style={{
          textAlign: "center",
          fontWeight: "700",
          color: "#2c3e50",
        }}
      >
        {businessName}
      </Title>

      <div className="queue-management-content">
        {/* Current Number Section - Enhanced */}
        <div className="number-display-section">
          <div className="number-display">
            <Button
              type="primary"
              size="large"
              onClick={() => currentNumber > 0 && handleDecrementWithLoading()}
              icon={<MinusOutlined style={{ fontSize: "28px" }} />}
              loading={loadingStates.decrement}
              disabled={loadingStates.decrement || currentNumber <= 0}
              style={{
                width: "20%",
                height: 70,
                backgroundColor: "#e74c3c",
                border: "none",
              }}
            />
            
            {isEditing ? (
              <Input
                type="number"
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleNumberChangeConfirm}
                onKeyDown={handleKeyDown}
                style={{
                  width: 120,
                  height: 70,
                  fontSize: "36px",
                  textAlign: "center",
                  margin: "0 10px",
                }}
              />
            ) : (
              <div 
                className="current-number-display"
                onClick={handleNumberClick}
                style={{ cursor: "pointer" }}
              >
                <span>{currentNumber}</span>
              </div>
            )}
            
            <Button
              type="primary"
              size="large"
              onClick={handleIncrementWithLoading}
              icon={<PlusOutlined style={{ fontSize: "28px" }} />}
              loading={loadingStates.increment}
              disabled={loadingStates.increment}
              style={{
                width: "20%",
                height: 70,
                backgroundColor: "#2ecc71",
                border: "none",
              }}
            />
          </div>
        </div>

        {/* Clinic Status Section */}
        <div className="clinic-status-section">
          <Button 
            type="default" 
            onClick={handleResetWithLoading} 
            className="reset-button"
            loading={loadingStates.reset}
            disabled={loadingStates.reset}
          >
            Reset Queue
          </Button>
          <div className="status-control">
            <Text strong className="section-label">
              set status
            </Text>
            <Switch
              checked={queueStatus}
              onChange={toggleActiveStatus}
              checkedChildren="OPEN"
              unCheckedChildren="CLOSED"
              style={{
                backgroundColor: queueStatus ? "#2ecc71" : "#e74c3c",
                minWidth: 70,
              }}
            />
          </div>
        </div>

        {/* Message Section */}
        <div className="message-section">
          <Input
            type="text"
            placeholder="send message to users.."
            onChange={(e) => setText(e.target.value)}
            value={textValue}
            onPressEnter={() =>
              textValue.length && setBusinessMessageForUsers(textValue, setText)
            }
            suffix={
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() =>
                  textValue.length &&
                  setBusinessMessageForUsers(textValue, setText)
                }
                style={{
                  height: "136%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "-12px",
                }}
              />
            }
          />
        </div>
        
        {/* Existing message display */}
        {businessMessage?.message?.length ? (
          <>
            <div style={{ display: "flex" }}>
              <div
                className="crossIconParent"
                onClick={() => {
                  setBusinessMessageForUsers("", setText);
                }}
              >
                <CloseOutlined
                  style={{
                    color: "red",
                    fontSize: "16px",
                  }}
                />
              </div>
              <div
                style={{
                  marginLeft: "5px",
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span className="messageArea">{businessMessage.message}</span>
              </div>
            </div>
            <div>
              <span className="timeArea">
                {" -"}
                {"sent at "}
                {businessMessage.time}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}
